<template>
  <div class="proa-textarea-container">
    <div class="proa-textarea-wrapper">
      <div
        v-html="innerValue"
        ref="proaTextareaProjector"
        class="proa-textarea-projector"
      />
      <textarea
        ref="proaTextarea"
        class="proa-textarea"
        :placeholder="placeholder"
        @focus="onFocus"
        @input="onInput"
        @keyup.enter="onKeyUpEnter"
        @keydown.delete="onKeyDownDelete"
        @keyup.delete="onKeyUpDelete"
        spellcheck="false"
        rows="1"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'RoomTextarea',
  props: {
    placeholder: String,
    defaultValue: String,
    quotationMode: Boolean
  },
  data() {
    return {
      innerValue: '',
      beforeDeletionValue: ''
    }
  },
  methods: {
    onFocus: function() {
      if (this.$refs.proaTextarea.value[this.beginningPositionInFocus()] == '>') {
        this.$emit('toggleQuotationMode', true)
      }
    },
    onInput: function(e) {
      this.adjustHeight()
      this.innerValue = this.$options.filters.formatChatPostContent(e.target.value)
      this.$emit('input', e.target.value)

      if (!this.quotationMode && this.$refs.proaTextarea.value[this.beginningPositionInFocus()] == '>') {
        this.$emit('toggleQuotationMode', true)
      }
    },
    onKeyUpEnter: function() {
      if (!this.quotationMode) return

      const position = this.$refs.proaTextarea.selectionStart

      // NOTE: 改行でないEnterKey(マルチバイト文字の確定)
      if (!this.$refs.proaTextarea.value[position - 1].match(/\n/)) return

      // NOTE: 改行した際に新しい行の先頭に>を挿入
      this.$refs.proaTextarea.value = this.$refs.proaTextarea.value.slice(0, position)
                                      + '>'
                                      + this.$refs.proaTextarea.value.slice(position, this.$refs.proaTextarea.value.length)
      this.fireInputTextarea()
      this.$refs.proaTextarea.setSelectionRange(position + 1, position + 1)
    },
    onKeyDownDelete: function() {
      this.beforeDeletionValue = this.$refs.proaTextarea.value
    },
    onKeyUpDelete: function() {
      if (!this.quotationMode) return

      // NOTE: 削除した文字が改行であり直後に>が来る場合には>を削除
      const position = this.$refs.proaTextarea.selectionStart
      if (this.beforeDeletionValue[position] === '\n' && this.beforeDeletionValue[position + 1] === '>') {
        this.$refs.proaTextarea.value = this.$refs.proaTextarea.value.slice(0, position)
                                        + this.$refs.proaTextarea.value.slice(position + 1, this.$refs.proaTextarea.value.length)
        this.fireInputTextarea()
        this.$refs.proaTextarea.setSelectionRange(position, position)
      }

      // NOTE: フォーカス行の先頭が>でなければquotationModeを解除
      if (this.quotationMode && this.$refs.proaTextarea.value[this.beginningPositionInFocus()] != '>') {
        this.$emit('toggleQuotationMode', false)
      }
    },
    adjustHeight: function() {
      const textarea = this.$refs.proaTextarea
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
      this.$refs.proaTextareaProjector.style.height = textarea.style.height
    },
    fireInputTextarea: function() {
      const evt = document.createEvent('HTMLEvents')
      evt.initEvent('input', true, true)
      this.$refs.proaTextarea.dispatchEvent(evt)
    },
    beginningPositionInFocus: function() {
      const tmpPosition = this.$refs.proaTextarea.value.lastIndexOf('\n', this.$refs.proaTextarea.selectionStart - 1)
      return tmpPosition === -1 ? 0 : tmpPosition + 1
    }
  },
  mounted() {
    if (!this.defaultValue) return
    this.innerValue = this.$options.filters.formatChatPostContent(this.defaultValue)
    this.$refs.proaTextarea.value = this.defaultValue
    this.adjustHeight()
  },
  watch: {
    quotationMode: function(value) {
      const position = this.beginningPositionInFocus()
      if (value) {
        if (this.$refs.proaTextarea.value[position] == '>') return

        this.$refs.proaTextarea.value = this.$refs.proaTextarea.value.slice(0, position)
                                        + '>'
                                        + this.$refs.proaTextarea.value.slice(position, this.$refs.proaTextarea.value.length)
        this.fireInputTextarea()
        this.$refs.proaTextarea.focus()
        this.$refs.proaTextarea.setSelectionRange(position + 1, position + 1)
      } else {
        if (this.$refs.proaTextarea.value[position] != '>') return

        this.$refs.proaTextarea.value = this.$refs.proaTextarea.value.slice(0, position)
                                        + this.$refs.proaTextarea.value.slice(position + 1, this.$refs.proaTextarea.value.length)
        this.fireInputTextarea()
        this.$refs.proaTextarea.focus()
        this.$refs.proaTextarea.setSelectionRange(position, position)
      }
    }
  }
}
</script>

<style lang="scss">
.proa-textarea-projector a {
  pointer-events: all;
}
</style>

<style scoped lang="scss">
@import "@/styles/variables";

.proa-textarea-container {
  display: flex;
  flex-direction: column;
}

.proa-textarea-wrapper {
  position: relative;
  min-height: 41px;
}

.proa-textarea-projector {
  pointer-events: none;
  position: absolute;
  z-index: 2;
  padding: 10px 15px;
  overflow: hidden;
  height: 41px;
  width: 100%;
  line-height: 20px;
  bottom: 0;
  border: 1px solid $dark-border-color;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  box-sizing: border-box;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.proa-textarea {
  z-index: 1;
  padding: 10px 15px;
  overflow: hidden;
  position: absolute;
  width: 100%;
  line-height: 20px;
  bottom: 0;
  border: 1px solid transparent;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  resize: none;
  outline: none;
  box-sizing: border-box;
  color: transparent;
  text-decoration-color: transparent;
  caret-color: black;

  &::placeholder {
    color: $light-text-color;
  }

  &::selection {
    color: transparent;
    background-color: $secondary-color;
  }
}
</style>
